import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChartOfAccountsModel } from '../../sifrarnik/services/chart-of-accounts-data.service';
import { CodebookItem } from '../../sifrarnik/services/codebook-lookup.service';
import {
  BankPostingRule,
  BankPostingRuleService,
  BankStatementRowValues,
} from '../../invoices/services/bank-posting-rule.service';

export interface BankPostingRuleDialogData {
  clientID: string;
  /** Company bank account (RACUN) the statement belongs to. */
  companyAccount: string;
  /** Row the popup was opened from; pre-fills the condition fields. */
  row?: Partial<BankStatementRowValues>;
  /** Existing rule when editing. */
  rule?: BankPostingRule;
  chartOfAccounts: ChartOfAccountsModel[];
  codebookItems: CodebookItem[];
}

type ConditionKey = keyof BankStatementRowValues;

@Component({
  selector: 'app-bank-posting-rule-dialog',
  templateUrl: './bank-posting-rule-dialog.component.html',
  styleUrls: ['./bank-posting-rule-dialog.component.css'],
})
export class BankPostingRuleDialogComponent implements OnInit {
  readonly conditions: { key: ConditionKey; label: string }[] = [
    { key: 'opis1', label: 'Opis_1' },
    { key: 'opis2', label: 'Opis_2' },
    { key: 'opis3', label: 'Opis_3' },
    { key: 'svrha', label: 'Svrha doznake' },
  ];

  values: BankStatementRowValues = { opis1: '', opis2: '', opis3: '', svrha: '' };
  /** Which of the pre-filled fields are part of the rule condition. */
  useAsCondition: { [key in ConditionKey]: boolean } = {
    opis1: false, opis2: false, opis3: false, svrha: false,
  };

  konto = '';
  partnerCode = '';
  saving = false;
  isEdit = false;

  constructor(
    public dialogRef: MatDialogRef<BankPostingRuleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BankPostingRuleDialogData,
    private ruleService: BankPostingRuleService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    const rule = this.data.rule;
    if (rule) {
      this.isEdit = true;
      for (const c of this.conditions) {
        const v = (rule[c.key] || '').toString();
        this.values[c.key] = v;
        this.useAsCondition[c.key] = !!v.trim();
      }
      this.konto = rule.konto || '';
      this.partnerCode = rule.partnerCode || '';
      return;
    }

    const row = this.data.row || {};
    for (const c of this.conditions) {
      this.values[c.key] = (row[c.key] || '').toString();
    }
    // Default condition: the partner account (Opis_1). Rows without an
    // account (bank fees) fall back to the payment description.
    if (this.values.opis1.trim()) {
      this.useAsCondition.opis1 = true;
    } else if (this.values.svrha.trim()) {
      this.useAsCondition.svrha = true;
    }
  }

  // ─── Konto ────────────────────────────────────────────────────────────────

  filteredAccounts(search: string): ChartOfAccountsModel[] {
    const q = (search || '').toLowerCase().trim();
    const all = this.data.chartOfAccounts || [];
    if (!q) return all.slice(0, 200);
    return all
      .filter((a) => a.konto.toLowerCase().includes(q) || a.nazivKonta.toLowerCase().includes(q))
      .slice(0, 200);
  }

  kontoDisplayFn = (value: string): string => {
    if (!value) return '';
    const hit = (this.data.chartOfAccounts || []).find((a) => a.konto === value);
    return hit ? `${hit.konto} – ${hit.nazivKonta}` : value;
  };

  // ─── Partner ──────────────────────────────────────────────────────────────

  filteredPartners(search: string): CodebookItem[] {
    const q = (search || '').toLowerCase().trim();
    const all = this.data.codebookItems || [];
    if (!q) return all.slice(0, 200);
    return all
      .filter(
        (c) =>
          (c.code || '').toLowerCase().includes(q) ||
          (c.companyName || '').toLowerCase().includes(q) ||
          (c.idNumber || '').toLowerCase().includes(q)
      )
      .slice(0, 200);
  }

  partnerDisplayFn = (value: string): string => {
    if (!value) return '';
    const hit = (this.data.codebookItems || []).find((c) => c.code === value);
    return hit ? `${hit.code} – ${hit.companyName || ''}` : value;
  };

  get partnerIdBroj(): string {
    if (!this.partnerCode) return '';
    return (this.data.codebookItems || []).find((c) => c.code === this.partnerCode)?.idNumber || '';
  }

  // ─── Save ─────────────────────────────────────────────────────────────────

  private buildRule(): BankPostingRule {
    const rule: BankPostingRule = {
      id: this.data.rule?.id,
      clientID: this.data.clientID,
      companyAccount: this.data.companyAccount,
      konto: this.konto.trim() || null,
      partnerCode: this.partnerCode.trim() || null,
      createdAt: this.data.rule?.createdAt,
    };
    for (const c of this.conditions) {
      rule[c.key] = this.useAsCondition[c.key] ? this.values[c.key].trim() || null : null;
    }
    return rule;
  }

  get isValid(): boolean {
    const rule = this.buildRule();
    return BankPostingRuleService.hasCondition(rule) && !!(rule.konto || rule.partnerCode);
  }

  async save(): Promise<void> {
    if (!this.isValid || this.saving) return;
    this.saving = true;
    try {
      const rule = this.buildRule();
      const saved = this.isEdit
        ? await this.ruleService.update(rule)
        : await this.ruleService.create(rule);
      this.dialogRef.close(saved);
    } catch (err) {
      console.error('[BankPostingRule] Save failed:', err);
      this.snackBar.open('Saving the rule failed, please try again', '', {
        duration: 5000,
        panelClass: ['white-snackbar'],
      });
    } finally {
      this.saving = false;
    }
  }
}
